import { exampleLink } from "app/config";
import { useTheme } from "app/shared";
import { FC, useState } from "react";
import HowToGetPrepared from "../HowToGetPrepared/HowToGetPrepared";
import ProjectDetails from "../ProjectDetails/ProjectDetails";
import ProjectMemo from "../ProjectMemo/ProjectMemo";
import Traction from "../ProjectMemo/Traction/Traction";
import SmartWhitelistInfoComponent from "../SmartWhitelistInfoComponent/SmartWhitelistInfoComponent";
import SpecialConditions from "../SpecialConditions/SpecialConditions";
import TokenUtility from "../TokenUtility/TokenUtility";
import { PurchaseDetails } from "../sale-widget/sale-widget";
import styles from "./IdoInfoComponent.module.scss";
import {
  filterVisibleMenuItems,
  getDefaultMenuItem,
} from "./hiddenSections.utils";

export interface IData {
  label: string;
  value: string;
}

export type MemoSection =
  | "memoHeader"
  | "description"
  | "team"
  | "teamPhoto"
  | "problemSolution"
  | "tokenInfo"
  | "traction"
  | "tokenUtility"
  | "backers"
  | "competitiveEdge"
  | "analysis"
  | "opportunitiesRisks";

interface IIdoInfoComponent {
  currentStage: string;
  projectDetailsTitle?: string;
  projectDetailsDescription?: string;
  projectDetailsIsLoading?: boolean;
  projectDetailsSubtitle?: string;
  projectDetailsYoutubeLink?: string;
  purchaseDetails: PurchaseDetails;
  handleCancelPurchase: () => void;
  isCancelSuccessful: boolean;
  tokenMetricsData: IData[];
  roundDetailsData: IData[];
  tokenUtilityData: IData[];
  memoData?: Array<Partial<Record<MemoSection, IData[]>>>;
  howToGetPreparedTitle?: string;
  howToGetPreparedDescription?: string;
  howToGetPreparedIsLoading?: boolean;
  specialConditionsTitle?: string;
  specialConditionsDescription?: string;
  ticker?: string;
  hiddenSections?: string[];
}

const IdoInfoComponent: FC<IIdoInfoComponent> = ({
  projectDetailsTitle,
  projectDetailsDescription,
  projectDetailsIsLoading,
  projectDetailsSubtitle,
  projectDetailsYoutubeLink,
  purchaseDetails,
  handleCancelPurchase,
  isCancelSuccessful,
  tokenMetricsData,
  roundDetailsData,
  tokenUtilityData,
  howToGetPreparedTitle,
  howToGetPreparedDescription,
  howToGetPreparedIsLoading,
  currentStage,
  specialConditionsTitle,
  specialConditionsDescription,
  memoData,
  ticker,
  hiddenSections,
}) => {
  const { theme } = useTheme();

  const baseMenuItems = [
    "SmartWhitelist",
    ...(ticker === "$iSWAP" ? [] : ["Special Conditions 🎁"]),
    "Project Details",
    "Round Details",
    "Token Utility",
    "How to Get Prepared",
  ];

  const hasMemoContent = Boolean(
    memoData &&
      memoData.some((section) => {
        if (!section || typeof section !== "object") return false;

        const entries = Object.entries(section);
        if (entries.length !== 1) return false;

        const [_, items] = entries[0];
        if (!Array.isArray(items) || items.length === 0) return false;

        return items.some((item) => {
          if (!item || typeof item !== "object") return false;
          const val = (item as any).value;
          return typeof val === "string" && val.trim() !== "";
        });
      })
  );

  const menuItems = filterVisibleMenuItems([...baseMenuItems], hiddenSections);

  if (hasMemoContent) {
    const i = menuItems.indexOf("Project Details");
    if (i !== -1) {
      menuItems.splice(i + 1, 0, "Project Memo");
    }
  }

  const contentComponents: { [key: string]: () => JSX.Element } = {
    "Project Details": () => (
      <ProjectDetails
        subtitle={projectDetailsSubtitle}
        title={projectDetailsTitle || ""}
        description={projectDetailsDescription || ""}
        isLoading={projectDetailsIsLoading || false}
        youtubeLink={projectDetailsYoutubeLink}
        imageUrl={ticker === "$SBX" ? exampleLink : undefined}
      />
    ),

    SmartWhitelist: () => (
      <SmartWhitelistInfoComponent
        currentStage={currentStage}
        handleCancelPurchase={handleCancelPurchase}
        isCancelSuccessful={isCancelSuccessful}
        currentPurchase={purchaseDetails}
      />
    ),

    ...(hasMemoContent
      ? {
          "Project Memo": () => <ProjectMemo data={memoData!} />,
        }
      : {}),

    ...(ticker !== "$iSWAP" && {
      "Special Conditions 🎁": () => (
        <SpecialConditions
          title={specialConditionsTitle || ""}
          description={specialConditionsDescription || ""}
        />
      ),
    }),

    "Round Details": () => (
      <>
        <Traction data={tokenMetricsData} isTokenMetrics />
        <Traction data={roundDetailsData} isKeyMetrics />
      </>
    ),

    "Token Utility": () => <TokenUtility tokenUtilityData={tokenUtilityData} />,

    "How to Get Prepared": () => (
      <HowToGetPrepared
        title={howToGetPreparedTitle || ""}
        description={howToGetPreparedDescription || ""}
        isLoading={howToGetPreparedIsLoading || false}
      />
    ),
  };

  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const activeItem =
    selectedItem && menuItems.includes(selectedItem)
      ? selectedItem
      : getDefaultMenuItem(menuItems, ticker);
  const setActiveItem = setSelectedItem;

  const ActiveComponent = contentComponents[activeItem] ?? (() => <></>);

  return (
    <div
      className={`${styles.container} ${
        theme === "dark" ? styles.darkTheme : ""
      }`}
    >
      <nav className={styles.sidebar}>
        <ul>
          {menuItems.map((item) => (
            <li
              key={item}
              className={`${activeItem === item ? styles.active : ""} ${
                theme === "dark" ? styles.darkTheme : ""
              }`}
              onClick={() => setActiveItem(item)}
            >
              {item}
            </li>
          ))}
        </ul>
      </nav>

      <div className={styles.content}>
        <ActiveComponent />
      </div>
    </div>
  );
};

export default IdoInfoComponent;
